import { isSameDay } from "date-fns"

const ONE_HOUR_IN_MILLISECONDS = 60 * 60 * 1000

type TimeBounds = {
	minTime: Date
	maxTime: Date
}

/**
 * Converts free-form input accepted by the browser's Date implementation into a valid date.
 *
 * @param value - The complete text entered or pasted by the user.
 * @returns The parsed date, or `undefined` when the input cannot be converted to a timestamp.
 */
export const parseDateInput = (value: string): Date | undefined => {
	// created by AI
	// Date supports ISO timestamps as well as the other date strings understood by the browser.
	const parsedDate = new Date(value.trim())

	return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate
}

const withTime = (date: Date, hours: number, minutes: number) => {
	const nextDate = new Date(date)
	nextDate.setHours(hours, minutes, 0, 0)
	return nextDate
}

const startOfDayTime = (date: Date) => withTime(date, 0, 0)
const endOfDayTime = (date: Date) => withTime(date, 23, 59)

const earlierDate = (a: Date, b: Date) => (a.getTime() <= b.getTime() ? a : b)

export const clampDateToNow = (date: Date, now = new Date()) =>
	date.getTime() > now.getTime() ? now : date

export const preserveTimeOnDateChange = (selectedDate: Date, previousDate: Date) => {
	if (isSameDay(selectedDate, previousDate)) {
		return selectedDate
	}

	const nextDate = new Date(selectedDate)
	nextDate.setHours(
		previousDate.getHours(),
		previousDate.getMinutes(),
		previousDate.getSeconds(),
		previousDate.getMilliseconds()
	)
	return nextDate
}

export const getFromPickerTimeBounds = (fromDate: Date, now = new Date()): TimeBounds => {
	const sameDayAsNow = isSameDay(fromDate, now)

	let maxTime = endOfDayTime(fromDate)
	if (sameDayAsNow) {
		maxTime = earlierDate(maxTime, now)
	}

	return {
		minTime: startOfDayTime(fromDate),
		maxTime,
	}
}

export const getToPickerTimeBounds = (toDate: Date, now = new Date()): TimeBounds => ({
	minTime: startOfDayTime(toDate),
	maxTime: isSameDay(toDate, now) ? now : endOfDayTime(toDate),
})

export const adjustRangeAfterFromChange = (
	selectedFromDate: Date,
	currentToDate: Date,
	now = new Date()
) => {
	const fromDate = clampDateToNow(selectedFromDate, now)
	const currentToDateClamped = clampDateToNow(currentToDate, now)

	if (fromDate.getTime() <= currentToDateClamped.getTime()) {
		return { fromDate, toDate: currentToDateClamped }
	}

	if (isSameDay(fromDate, currentToDateClamped)) {
		return {
			fromDate: new Date(currentToDateClamped.getTime() - ONE_HOUR_IN_MILLISECONDS),
			toDate: currentToDateClamped,
		}
	}

	return {
		fromDate,
		toDate: new Date(Math.min(fromDate.getTime() + ONE_HOUR_IN_MILLISECONDS, now.getTime())),
	}
}

export const adjustRangeAfterToChange = (
	currentFromDate: Date,
	selectedToDate: Date,
	now = new Date()
) => {
	const toDate = clampDateToNow(selectedToDate, now)

	if (currentFromDate.getTime() <= toDate.getTime()) {
		return { fromDate: currentFromDate, toDate }
	}

	if (isSameDay(currentFromDate, toDate)) {
		return {
			fromDate: currentFromDate,
			toDate: new Date(
				Math.min(currentFromDate.getTime() + ONE_HOUR_IN_MILLISECONDS, now.getTime())
			),
		}
	}

	return {
		fromDate: new Date(toDate.getTime() - ONE_HOUR_IN_MILLISECONDS),
		toDate,
	}
}

export const isValidRangeForSearch = (fromDate: Date, toDate: Date, now = new Date()) =>
	toDate.getTime() <= now.getTime() && fromDate.getTime() <= toDate.getTime()
